import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Palette, User, HardDrive } from 'lucide-react';
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';
import * as THREE from 'three';
import { useAuth } from '../contexts/AuthContext';

// Load logo from public folder
const logo = '/logo.gif';

// ===== HARDCODED 4 SCENES WITH PLY FILES =====
const HARDCODED_SCENES = [
  {
    sceneId: 'maggie',
    sceneName: 'Maggie',
    sceneDescription: 'Beautiful residential space',
    plyUrl: '/Maggie.ply',
    category: 'Indoor',
    thumbnail: null,
    credits: 'CR3ATE.AI',
    location: 'Residential',
  },
  {
    sceneId: 'wingwalker',
    sceneName: 'Wing Walker',
    sceneDescription: 'Aerial scene reconstruction',
    plyUrl: '/WingWalker.ply',
    category: 'Aerial',
    thumbnail: null,
    credits: 'CR3ATE.AI',
    location: 'Outdoor',
  },
  {
    sceneId: 'kusadak',
    sceneName: 'Kusadak',
    sceneDescription: 'Living room interior',
    plyUrl: '/Kusadak.ply',
    category: 'Indoor',
    thumbnail: null,
    credits: 'CR3ATE.AI',
    location: 'Living Room',
  },
  {
    sceneId: 'ezra',
    sceneName: 'Ezra Gallery',
    sceneDescription: 'Art gallery space',
    plyUrl: '/Ezra.ply',
    category: 'Art',
    thumbnail: null,
    credits: 'CR3ATE.AI',
    location: 'Gallery',
  },
];

interface Scene {
  sceneId: string;
  sceneName: string;
  sceneDescription: string;
  plyUrl: string;
  category: string;
  thumbnail: string | null;
  credits: string;
  location: string;
}

// Camera positions (try to stay close to GAUS viewer)
const getCameraPositionForScene = (sceneId: string): [number, number, number] => {
  switch (sceneId.toLowerCase()) {
    case 'maggie':
      return [2.1, 0, -0.5];
    case 'kusadak':
      return [0.8, 0, -1];
    case 'ezra':
      return [-1.81, 0, -1.19];
    case 'wingwalker':
      return [0, 10, 30];
    default:
      return [0, 0, 5];
  }
};

// ===== FIRST PERSON CONTROLS (pointer lock + WASD) =====
class FirstPersonControls {
  camera: THREE.Camera;
  domElement: HTMLElement;

  enabled = true;

  movementSpeed = 5.0;
  lookSpeed = 1.0;
  sprintMultiplier = 2.5;

  private moveForward = false;
  private moveBackward = false;
  private moveLeft = false;
  private moveRight = false;
  private moveUp = false;
  private moveDown = false;
  private isSprinting = false;

  private yaw = 0;
  private pitch = 0;
  private isPointerLocked = false;

  private deltaX = 0;
  private deltaY = 0;

  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnKeyDown: (e: KeyboardEvent) => void;
  private boundOnKeyUp: (e: KeyboardEvent) => void;
  private boundOnPointerDown: (e: PointerEvent) => void;
  private boundOnPointerLockChange: () => void;

  constructor(camera: THREE.Camera, domElement: HTMLElement) {
    this.camera = camera;
    this.domElement = domElement;

    const euler = new THREE.Euler().setFromQuaternion(
      this.camera.quaternion,
      'YXZ'
    );
    this.yaw = euler.y;
    this.pitch = euler.x;

    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnKeyUp = this.onKeyUp.bind(this);
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    this.boundOnPointerLockChange = this.onPointerLockChange.bind(this);

    this.domElement.addEventListener('pointerdown', this.boundOnPointerDown);
    document.addEventListener('pointerlockchange', this.boundOnPointerLockChange);

    window.addEventListener('mousemove', this.boundOnMouseMove);
    window.addEventListener('keydown', this.boundOnKeyDown);
    window.addEventListener('keyup', this.boundOnKeyUp);
  }

  private onPointerDown(_e: PointerEvent) {
    if (!this.isPointerLocked) {
      this.domElement.requestPointerLock?.();
    }
  }

  private onPointerLockChange() {
    this.isPointerLocked = document.pointerLockElement === this.domElement;
    if (this.isPointerLocked) {
      this.deltaX = 0;
      this.deltaY = 0;
    }
  }

  private onMouseMove(e: MouseEvent) {
    if (!this.enabled || !this.isPointerLocked) return;
    this.deltaX += e.movementX;
    this.deltaY += e.movementY;
  }

  private onKeyDown(e: KeyboardEvent) {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = true;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = true;
        break;
      case 'KeyA':
        this.moveLeft = true;
        break;
      case 'KeyD':
        this.moveRight = true;
        break;
      case 'Space':
      case 'KeyE':
        this.moveUp = true;
        e.preventDefault();
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
      case 'KeyQ':
        this.moveDown = true;
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.isSprinting = true;
        break;
    }
  }

  private onKeyUp(e: KeyboardEvent) {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.moveForward = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.moveBackward = false;
        break;
      case 'KeyA':
        this.moveLeft = false;
        break;
      case 'KeyD':
        this.moveRight = false;
        break;
      case 'Space':
      case 'KeyE':
        this.moveUp = false;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
      case 'KeyQ':
        this.moveDown = false;
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.isSprinting = false;
        break;
    }
  }

  update(delta: number) {
    if (!this.enabled) return;

    // 1) Mouse look
    const sensitivity = this.lookSpeed * 0.002;
    const dx = this.deltaX;
    const dy = this.deltaY;
    this.deltaX = 0;
    this.deltaY = 0;

    this.yaw -= dx * sensitivity;
    this.pitch -= dy * sensitivity;

    const maxPitch = Math.PI / 2 - 0.05;
    this.pitch = Math.max(-maxPitch, Math.min(maxPitch, this.pitch));

    const q = new THREE.Quaternion();
    q.setFromEuler(new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ'));
    this.camera.quaternion.copy(q);

    // 2) Movement
    let speed = this.movementSpeed * delta;
    if (this.isSprinting) speed *= this.sprintMultiplier;

    const forward = new THREE.Vector3();
    const right = new THREE.Vector3();

    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();

    right.crossVectors(forward, this.camera.up).normalize().negate();

    if (this.moveForward) {
      this.camera.position.addScaledVector(forward, speed);
    }
    if (this.moveBackward) {
      this.camera.position.addScaledVector(forward, -speed);
    }
    if (this.moveLeft) {
      this.camera.position.addScaledVector(right, -speed);
    }
    if (this.moveRight) {
      this.camera.position.addScaledVector(right, speed);
    }
    if (this.moveUp) {
      this.camera.position.y += speed;
    }
    if (this.moveDown) {
      this.camera.position.y -= speed;
    }
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this.boundOnPointerDown);
    document.removeEventListener('pointerlockchange', this.boundOnPointerLockChange);
    window.removeEventListener('mousemove', this.boundOnMouseMove);
    window.removeEventListener('keydown', this.boundOnKeyDown);
    window.removeEventListener('keyup', this.boundOnKeyUp);
  }
}

const TheCollectiv3_PLY: React.FC = () => {
  const navigate = useNavigate();
  const { authenticated } = useAuth(); // currently unused, but kept if you need later

  const [scenes] = useState<Scene[]>(HARDCODED_SCENES);
  const [currentView, setCurrentView] = useState<'gallery' | '3d-viewer'>('gallery');
  const [selectedScene, setSelectedScene] = useState<Scene | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const modelContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<GaussianSplats3D.Viewer | null>(null);
  const controlsRef = useRef<FirstPersonControls | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const clockRef = useRef<THREE.Clock>(new THREE.Clock());

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return <Palette className="w-3 h-3" />;
      case 'people':
        return <User className="w-3 h-3" />;
      default:
        return <HardDrive className="w-3 h-3" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'outdoor':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'indoor':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'people':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      default:
        return 'bg-[#0CB4E4]/20 text-[#0CB4E4] border-[#0CB4E4]/30';
    }
  };

  const getMovementSpeedForScene = (sceneId: string): number => {
    switch (sceneId.toLowerCase()) {
      case 'maggie':
        return 3.0;
      case 'kusadak':
        return 4.0;
      case 'ezra':
        return 5.0;
      case 'wingwalker':
        return 6.0;
      default:
        return 8.0;
    }
  };

  // Animation loop: only drive controls, Viewer does its own render loop
  const animate = () => {
    const controls = controlsRef.current;
    const delta = clockRef.current.getDelta();

    if (controls) {
      controls.update(delta);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleSceneClick = async (scene: Scene) => {
    console.log(`[PLY Test] Loading ${scene.sceneName}`);
    console.time(`[PLY Test] ${scene.sceneName} total`);

    // Cleanup
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }
    if (viewerRef.current) {
      try {
        viewerRef.current.dispose();
      } catch (e) {
        console.warn('[PLY Test] Error disposing viewer', e);
      }
      viewerRef.current = null;
    }

    setCurrentView('3d-viewer');
    setSelectedScene(scene);
    setModelLoading(true);
    setLoadingProgress(0);

    await new Promise((r) => setTimeout(r, 50));

    const container = modelContainerRef.current;
    if (!container) {
      console.error('[PLY Test] No container');
      return;
    }

    try {
      const initialPosition = getCameraPositionForScene(scene.sceneId);

      const viewer = new GaussianSplats3D.Viewer({
        rootElement: container,
        cameraUp: [0, 1, 0],
        initialCameraPosition: initialPosition,
        initialCameraLookAt: [0, 0, 0],
        sharedMemoryForWorkers: false,
        selfDrivenMode: true,
      });

      // Disable built-in controls if exposed
      const vAny = viewer as any;
      if (vAny.controls) {
        vAny.controls.enabled = false;
      }
      if (typeof vAny.setCameraMode === 'function') {
        try {
          vAny.setCameraMode('none');
        } catch {
          // ignore
        }
      }

      viewerRef.current = viewer;

      console.log('[PLY Test] addSplatScene', scene.plyUrl);

      await viewer.addSplatScene(scene.plyUrl, {
        progressiveLoad: true,
        onProgress: (p: number) => {
          const pct = Math.round(p * 100);
          setLoadingProgress(pct);
        },
      });

      console.log('[PLY Test] Scene loaded');
      console.timeEnd(`[PLY Test] ${scene.sceneName} total`);

      viewer.start();

      const camera: THREE.Camera = (viewer as any).camera;
      const rendererDom: HTMLElement =
        (viewer as any).renderer?.domElement || container;

      const controls = new FirstPersonControls(camera, rendererDom);
      controls.movementSpeed = getMovementSpeedForScene(scene.sceneId);
      controls.lookSpeed = 1.0;
      controlsRef.current = controls;

      clockRef.current.start();
      animationFrameRef.current = requestAnimationFrame(animate);

      setModelLoading(false);
      setLoadingProgress(100);
    } catch (err) {
      console.error('[PLY Test] Error loading scene', err);
      setModelLoading(false);
      alert(
        `Failed to load scene: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      );
    }
  };

  const close3DViewer = () => {
    console.log('[PLY Test] Close viewer');

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }
    if (viewerRef.current) {
      try {
        viewerRef.current.dispose();
      } catch (e) {
        console.warn('[PLY Test] Error disposing viewer', e);
      }
      viewerRef.current = null;
    }

    setCurrentView('gallery');
    setSelectedScene(undefined);
    setModelLoading(false);
    setLoadingProgress(0);
  };

  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && currentView === '3d-viewer') {
        close3DViewer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentView]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      if (viewerRef.current) {
        try {
          viewerRef.current.dispose();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  // ===== UI PIECES =====
  const renderGalleryView = () => (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1221] via-[#161513] to-[#0B1221]">
      <div className="border-b border-white/10 bg-[#0B1221]/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <img src={logo} alt="CR3ATE.AI" className="h-8 w-8 object-contain" />
              <div>
                <h1 className="text-xl font-light text-[#0CB4E4]">
                  PLY Performance Test
                </h1>
                <p className="text-xs text-gray-400">
                  Same navigation feel as GAUS viewer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-white mb-2">
            3D Scene Gallery
          </h2>
          <p className="text-gray-400">
            Click any scene • check console for load times
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {scenes.map((scene) => (
            <div
              key={scene.sceneId}
              onClick={() => handleSceneClick(scene)}
              className="group relative bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-[#0CB4E4]/50 transition-all duration-300 cursor-pointer hover:scale-105"
            >
              <div className="aspect-video bg-gradient-to-br from-[#0CB4E4]/20 to-[#0CB4E4]/20 flex items-center justify-center">
                <Play className="w-12 h-12 text-white/60 group-hover:text-[#0CB4E4] transition-colors" />
              </div>
              <div className="p-4 space-y-3">
                <h3 className="text-lg font-semibold text-white group-hover:text-[#0CB4E4] transition-colors">
                  {scene.sceneName}
                </h3>
                <p className="text-sm text-gray-400 line-clamp-2">
                  {scene.sceneDescription}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getCategoryColor(
                      scene.category
                    )}`}
                  >
                    {getCategoryIcon(scene.category)}
                    {scene.category}
                  </span>
                  {scene.location && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <span>📍</span>
                      <span>{scene.location}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 text-sm text-gray-300">
          <p className="font-semibold text-[#0CB4E4] mb-2">
            Navigation (like GAUS viewer)
          </p>
          <p>• Click inside viewer once to capture mouse.</p>
          <p>• WASD – move, Q/E or Space/Shift – up/down.</p>
          <p>• Move mouse to look around.</p>
          <p>• ESC – exit viewer.</p>
        </div>
      </div>
    </div>
  );

  const renderLoadingUI = () => (
    <div className="fixed inset-0 z-[1002] bg-black/80 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-[#161513]/95 rounded-2xl border border-[#0CB4E4]/30 p-8 max-w-md w-full mx-4 shadow-2xl">
        <h2 className="text-2xl font-semibold text-white mb-6 text-center">
          Loading {selectedScene?.sceneName}...
        </h2>
        <div className="space-y-3">
          <div className="relative w-full h-4 rounded-full bg-white/10 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/85 transition-all duration-300 ease-out"
              style={{ width: `${loadingProgress}%` }}
            />
            <div className="absolute inset-0 grid place-items-center">
              <span className="text-xs font-medium text-white/90 drop-shadow">
                {loadingProgress}%
              </span>
            </div>
          </div>
          <p className="text-center text-sm text-gray-400">
            Loading PLY file... check console for timing
          </p>
        </div>
      </div>
    </div>
  );

  const render3DViewer = () => (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      <div className="fixed top-4 right-4 z-[1001]">
        <button
          onClick={close3DViewer}
          className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
        >
          <span className="text-lg">✕</span>
          <span>Close</span>
          <span className="text-xs text-gray-400">(ESC)</span>
        </button>
      </div>

      <div className="fixed top-4 left-4 z-[1001] flex items-center gap-2">
        <img src={logo} alt="CR3ATE.AI" className="h-10 w-10 object-contain" />
        <div>
          <h1 className="font-light text-xl text-[#0CB4E4]">
            PLY Performance Test
          </h1>
          {selectedScene && (
            <p className="text-xs text-gray-400">
              {selectedScene.sceneName} • first-person navigation
            </p>
          )}
        </div>
      </div>

      {!modelLoading && (
        <div className="fixed top-20 left-4 z-[1001] bg-[#161513]/90 backdrop-blur-sm text-white px-4 py-3 rounded-xl border border-[#0CB4E4]/30 max-w-xs text-xs">
          <p className="font-medium mb-1">🎮 Controls</p>
          <p>Click once to capture mouse</p>
          <p>WASD – move, Q/E or Space/Shift – up/down</p>
          <p>Move mouse – look around</p>
          <p>ESC – exit viewer</p>
        </div>
      )}

      {modelLoading && renderLoadingUI()}

      <div
        ref={modelContainerRef}
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 1000, backgroundColor: 'black' }}
      />
    </div>
  );

  return (
    <>
      {currentView === 'gallery' && renderGalleryView()}
      {currentView === '3d-viewer' && render3DViewer()}
    </>
  );
};

export default TheCollectiv3_PLY;